import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Download,
  ChevronRight,
  ChevronLeft,
  Users,
  Settings,
  Eye,
  Upload,
  Info,
} from 'lucide-react';
import { DotyposService } from '@/lib/integrations/dotypos-service';
import {
  DotyposEmployee,
  DotyposConfig,
  ImportConflict,
  ImportSummary,
  ImportStep,
  ImportWizardState,
  AttendanceSyncMode,
  CreateShiftsSyncResult,
  MatchExistingSyncResult,
  EmailIssue,
  EmailResolution,
  EmailResolutionType,
  SmartMatch,
  SmartMatchResolution,
} from '@/types/dotypos';
import { toast } from '@/hooks/ui/use-toast';
import { cn } from '@/lib/core/utils';
import { useDotyposAttendanceSync } from '@/hooks/integrations/useDotyposAttendanceSync';
import { supabase } from '@/integrations/supabase/client';
import { RefreshCw, Calendar, Link2, Mail, UserX, Zap, Check, X } from 'lucide-react';

interface DotyposImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  businessName?: string;
  defaultRoleId?: string | null;
  onImportComplete?: (summary: ImportSummary) => void;
}

export const DotyposImportDialog: React.FC<DotyposImportDialogProps> = ({
  isOpen,
  onClose,
  businessId,
  businessName = 'System',
  defaultRoleId,
  onImportComplete,
}) => {
  const [wizardState, setWizardState] = useState<ImportWizardState>({
    step: 'fetch',
    fetchedEmployees: [],
    conflicts: [],
    conflictResolutions: new Map(),
    smartMatches: [],
    smartMatchResolutions: new Map(),
    emailIssues: [],
    emailResolutions: new Map(),
    isLoading: false,
    error: null,
  });

  const [config, setConfig] = useState<DotyposConfig | null>(null);
  const [skipDeleted, setSkipDeleted] = useState(true);
  const [skipDisabled, setSkipDisabled] = useState(false);
  const [syncMode, setSyncMode] = useState<AttendanceSyncMode>('create_shifts');
  const [attendanceSyncResult, setAttendanceSyncResult] = useState<
    CreateShiftsSyncResult | MatchExistingSyncResult | null
  >(null);
  const [existingEmails, setExistingEmails] = useState<Set<string>>(new Set());
  const [existingEmployeesList, setExistingEmployeesList] = useState<Array<{ id: string; name: string; email: string }>>([]);

  const { isSyncing, syncAttendanceWithShifts } = useDotyposAttendanceSync();

  // Load saved config on mount and auto-fetch
  useEffect(() => {
    if (isOpen && businessId) {
      loadSavedConfigAndFetch();
    }
  }, [isOpen, businessId]);

  const loadSavedConfigAndFetch = async () => {
    setWizardState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const loadedConfig = await DotyposService.loadConfig(businessId);

      if (!loadedConfig) {
        throw new Error('No Dotypos configuration found. Please configure Dotypos connection in Settings first.');
      }

      setConfig(loadedConfig);
      setSkipDeleted(loadedConfig.import_settings?.skipDeletedEmployees ?? true);
      setSkipDisabled(!(loadedConfig.import_settings?.importDisabledEmployees ?? true));

      // Auto-fetch employees
      await fetchEmployeesWithConfig(loadedConfig);
    } catch (error: any) {
      toast.error('Configuration Error', {
        description: error.message || 'Failed to load Dotypos configuration',
      });
      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const fetchEmployeesWithConfig = async (configToUse: DotyposConfig) => {
    try {
      // Fetch existing employees to get their emails for validation and for assimilation
      const { data: existingEmployeesData } = await supabase
        .from('employees')
        .select('id, name, email')
        .eq('business_id', businessId);

      if (existingEmployeesData) {
        const emailSet = new Set(
          existingEmployeesData.map(e => e.email.toLowerCase())
        );
        setExistingEmails(emailSet);
        setExistingEmployeesList(existingEmployeesData);
      }

      // Fetch employees
      const employees = await DotyposService.fetchEmployees({
        client_id: configToUse.client_id,
        client_secret: configToUse.client_secret,
        cloud_id: configToUse.cloud_id,
        business_id: configToUse.business_id,
        refresh_token: configToUse.refresh_token,
      });

      // Filter based on settings
      const filtered = DotyposService.filterEmployees(
        employees,
        skipDeleted,
        skipDisabled
      );

      if (filtered.length === 0) {
        toast.error('No Employees Found', {
          description: 'No employees match the import criteria',
        });
        setWizardState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      // Detect conflicts
      const conflicts = await DotyposService.detectConflicts(businessId, filtered);

      // Detect smart matches (name-based) for ALL non-conflicted employees
      const smartMatches = await DotyposService.detectSmartMatches(businessId, filtered, conflicts);

      // Detect email issues for employees not in conflicts AND not in smart matches
      // (smart match employees will get email issues added if they reject the match)
      const smartMatchedIds = new Set(smartMatches.map(m => m.dotyposEmployee.id));
      const employeesNeedingEmailResolution = filtered.filter(
        emp => !smartMatchedIds.has(emp.id)
      );
      const emailIssues = await DotyposService.detectEmailIssues(businessId, employeesNeedingEmailResolution, conflicts);

      setWizardState({
        ...wizardState,
        step: 'review',
        fetchedEmployees: filtered,
        conflicts,
        smartMatches,
        emailIssues,
        isLoading: false,
      });

      const issueCount = conflicts.length + smartMatches.length + emailIssues.length;
      toast.success('Employees Fetched', {
        description: `Found ${filtered.length} employees. ${smartMatches.length} smart match(es), ${emailIssues.length} email issue(s) detected.`,
      });
    } catch (error: any) {
      toast.error('Fetch Failed', {
        description: error.message || 'Failed to fetch employees from Dotypos',
      });
      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const handleConflictResolution = (dotyposId: number, action: 'skip' | 'update' | 'create') => {
    setWizardState((prev) => {
      const newResolutions = new Map(prev.conflictResolutions);
      newResolutions.set(dotyposId, action);
      return { ...prev, conflictResolutions: newResolutions };
    });
  };

  const handleSmartMatchResolution = (dotyposId: number, resolution: SmartMatchResolution) => {
    setWizardState((prev) => {
      const newResolutions = new Map(prev.smartMatchResolutions);
      newResolutions.set(dotyposId, resolution);
      return { ...prev, smartMatchResolutions: newResolutions };
    });
  };

  const handleEmailResolution = (
    dotyposId: number,
    resolutionType: EmailResolutionType,
    manualEmail?: string,
    linkToEmployeeId?: string
  ) => {
    setWizardState((prev) => {
      const newResolutions = new Map(prev.emailResolutions);
      newResolutions.set(dotyposId, {
        dotyposId,
        resolutionType,
        manualEmail,
        linkToEmployeeId,
      });
      return { ...prev, emailResolutions: newResolutions };
    });
  };

  const getEmailResolution = (dotyposId: number): EmailResolution | undefined => {
    return wizardState.emailResolutions.get(dotyposId);
  };

  const allEmailIssuesResolved = (): boolean => {
    return wizardState.emailIssues.every((issue) => {
      const resolution = wizardState.emailResolutions.get(issue.dotyposEmployee.id);
      if (!resolution) return false;
      if (resolution.resolutionType === 'manual') {
        if (!resolution.manualEmail) return false;
        // Check for validation errors
        const error = getEmailValidationError(resolution.manualEmail, issue.dotyposEmployee.id);
        if (error) return false;
      }
      if (resolution.resolutionType === 'link_existing' && !resolution.linkToEmployeeId) return false;
      if (resolution.resolutionType === 'assimilate' && !resolution.linkToEmployeeId) return false;
      return true;
    });
  };

  // Get employees available for assimilation (not already being used by other resolutions)
  const getAvailableEmployeesForAssimilation = (currentDotyposId: number): Array<{ id: string; name: string; email: string }> => {
    const usedEmployeeIds = new Set<string>();

    // Collect all employee IDs that are already being linked/assimilated
    for (const [dotyposId, resolution] of wizardState.emailResolutions.entries()) {
      if (dotyposId !== currentDotyposId && resolution.linkToEmployeeId) {
        usedEmployeeIds.add(resolution.linkToEmployeeId);
      }
    }

    // Also check conflict resolutions for 'update' actions
    for (const conflict of wizardState.conflicts) {
      const conflictRes = wizardState.conflictResolutions.get(conflict.dotyposEmployee.id);
      if (conflictRes === 'update') {
        usedEmployeeIds.add(conflict.existingEmployee.id);
      }
    }

    return existingEmployeesList.filter(emp => !usedEmployeeIds.has(emp.id));
  };

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Check if email already exists in TagInn
  const isEmailInTagInn = (email: string): boolean => {
    return existingEmails.has(email.toLowerCase());
  };

  // Check if email is used by another resolution in the batch
  const isEmailUsedInBatch = (email: string, currentDotyposId: number): boolean => {
    const emailLower = email.toLowerCase();

    // Check other manual resolutions
    for (const [dotyposId, resolution] of wizardState.emailResolutions.entries()) {
      if (dotyposId !== currentDotyposId &&
          resolution.resolutionType === 'manual' &&
          resolution.manualEmail?.toLowerCase() === emailLower) {
        return true;
      }
    }

    // Check Dotypos employees that have this email and aren't being resolved
    const employeesWithEmail = wizardState.fetchedEmployees.filter(
      emp => emp.email?.toLowerCase() === emailLower && emp.id !== currentDotyposId
    );

    // If any of these employees don't have an email resolution (meaning they'll use their original email)
    for (const emp of employeesWithEmail) {
      const resolution = wizardState.emailResolutions.get(emp.id);
      if (!resolution || (resolution.resolutionType !== 'manual' && resolution.resolutionType !== 'skip')) {
        return true;
      }
    }

    return false;
  };

  // Get validation error for an email
  const getEmailValidationError = (email: string, dotyposId: number): string | null => {
    if (!email) return null;
    if (!isValidEmail(email)) return 'Please enter a valid email address';
    if (isEmailInTagInn(email)) return 'This email already exists in TagInn';
    if (isEmailUsedInBatch(email, dotyposId)) return 'This email is already used by another employee in this import';
    return null;
  };

  const handleImport = async () => {
    setWizardState((prev) => ({ ...prev, step: 'import', isLoading: true, error: null }));

    try {
      const summary = await DotyposService.importEmployees(
        businessId,
        wizardState.fetchedEmployees,
        wizardState.conflicts,
        wizardState.conflictResolutions,
        wizardState.smartMatches,
        wizardState.smartMatchResolutions,
        wizardState.emailResolutions,
        defaultRoleId
      );

      setWizardState((prev) => ({
        ...prev,
        step: 'sync_attendance', // Move to attendance sync step
        isLoading: false,
        summary,
      }));

      toast.success('Import Complete', {
        description: `Imported ${summary.created} new employees, updated ${summary.updated}, skipped ${summary.skipped}. Now syncing attendance...`,
      });
    } catch (error: any) {
      toast.error('Import Failed', {
        description: error.message || 'Failed to import employees',
      });
      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const handleSyncAttendance = async () => {
    setWizardState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // First-time sync: fetch ALL historical attendance data
      // Using 'all' sync_mode to get complete history from Dotypos
      const result = await syncAttendanceWithShifts(
        businessId,
        syncMode,
        'all' // Sync all historical data on first import
      );

      if (result) {
        setAttendanceSyncResult(result);
        setWizardState((prev) => ({
          ...prev,
          step: 'complete',
          isLoading: false,
        }));

        // Now call the completion callback
        if (onImportComplete && wizardState.summary) {
          onImportComplete(wizardState.summary);
        }
      } else {
        throw new Error('Attendance sync failed');
      }
    } catch (error: any) {
      toast.error('Attendance Sync Failed', {
        description: error.message || 'Failed to sync attendance data',
      });
      setWizardState((prev) => ({
        ...prev,
        isLoading: false,
        error: error.message,
      }));
    }
  };

  const handleClose = () => {
    // Reset state
    setWizardState({
      step: 'fetch',
      fetchedEmployees: [],
      conflicts: [],
      conflictResolutions: new Map(),
      smartMatches: [],
      smartMatchResolutions: new Map(),
      emailIssues: [],
      emailResolutions: new Map(),
      isLoading: false,
      error: null,
    });
    setConfig(null);
    onClose();
  };

  const renderStepIndicator = () => {
    // Build steps dynamically - only show resolve_emails if there are issues
    const baseSteps = [
      { id: 'fetch', label: 'Fetch', icon: Download },
      { id: 'review', label: 'Review', icon: Eye },
    ];

    // Add resolve_emails step if there are email issues
    if (wizardState.emailIssues.length > 0) {
      baseSteps.push({ id: 'resolve_emails', label: 'Emails', icon: Mail });
    }

    baseSteps.push(
      { id: 'import', label: 'Import', icon: Upload },
      { id: 'sync_attendance', label: 'Sync', icon: RefreshCw },
      { id: 'complete', label: 'Done', icon: CheckCircle2 }
    );

    const steps = baseSteps;

    const currentIndex = steps.findIndex((s) => s.id === wizardState.step);

    return (
      <div className="flex items-center justify-between mb-6">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors',
                    isActive && 'border-primary bg-primary text-primary-foreground',
                    isCompleted && 'border-green-500 bg-green-500 text-white',
                    !isActive && !isCompleted && 'border-gray-300 bg-white'
                  )}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <span className={cn('text-xs mt-1', isActive && 'font-semibold')}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2',
                    isCompleted ? 'bg-green-500' : 'bg-gray-300'
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderFetchStep = () => (
    <div className="space-y-4 flex flex-col items-center justify-center py-8">
      <Loader2 className="w-12 h-12 animate-spin text-primary" />
      <h3 className="text-lg font-semibold">Fetching Employees...</h3>
      <p className="text-sm text-muted-foreground text-center">
        Loading employee data from Dotypos
      </p>
      <Progress value={undefined} className="w-full" />

      {wizardState.error && (
        <Alert variant="destructive" className="mt-4">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            {wizardState.error}
            <br />
            <span className="text-xs mt-2 inline-block">
              Please configure Dotypos connection in Settings.
            </span>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  const renderReviewStep = () => {
    const nonConflictEmployees = wizardState.fetchedEmployees.filter(
      (emp) => !wizardState.conflicts.some((c) => c.dotyposEmployee.id === emp.id)
    );

    return (
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Review Import</h3>
            <p className="text-sm text-muted-foreground">
              {wizardState.fetchedEmployees.length} employees ready to import
            </p>
          </div>
          <Badge>
            {wizardState.conflicts.length} conflicts
          </Badge>
        </div>

        {/* Conflicts */}
        {wizardState.conflicts.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-500" />
              Conflicts Detected
            </h4>
            {wizardState.conflicts.map((conflict) => (
              <Card key={conflict.dotyposEmployee.id} className="border-orange-200">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">
                        {conflict.dotyposEmployee.name}
                      </CardTitle>
                      <CardDescription>{conflict.dotyposEmployee.email}</CardDescription>
                    </div>
                    <Badge variant={conflict.conflictReason === 'dotypos_id_match' ? 'secondary' : 'destructive'}>
                      {conflict.conflictReason === 'dotypos_id_match' ? 'Already Imported' : 'Duplicate Email'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <p className="font-semibold mb-1">From Dotypos:</p>
                      <p className="text-muted-foreground">{conflict.dotyposEmployee.name}</p>
                      <p className="text-muted-foreground">{conflict.dotyposEmployee.email}</p>
                      <p className="text-xs text-muted-foreground">ID: {conflict.dotyposEmployee.id}</p>
                    </div>
                    <div>
                      <p className="font-semibold mb-1">Existing in TagInn:</p>
                      <p className="text-muted-foreground">{conflict.existingEmployee.name}</p>
                      <p className="text-muted-foreground">{conflict.existingEmployee.email}</p>
                      {conflict.existingEmployee.dotypos_employee_id && (
                        <p className="text-xs text-muted-foreground">
                          Dotypos ID: {conflict.existingEmployee.dotypos_employee_id}
                        </p>
                      )}
                    </div>
                  </div>

                  <RadioGroup
                    value={
                      wizardState.conflictResolutions.get(conflict.dotyposEmployee.id) ||
                      conflict.suggestedAction
                    }
                    onValueChange={(value) =>
                      handleConflictResolution(
                        conflict.dotyposEmployee.id,
                        value as 'skip' | 'update' | 'create'
                      )
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="skip" id={`skip-${conflict.dotyposEmployee.id}`} />
                      <Label htmlFor={`skip-${conflict.dotyposEmployee.id}`} className="font-normal">
                        Skip this employee
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="update" id={`update-${conflict.dotyposEmployee.id}`} />
                      <Label htmlFor={`update-${conflict.dotyposEmployee.id}`} className="font-normal">
                        Update existing employee with Dotypos data
                      </Label>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Smart matches */}
        {wizardState.smartMatches.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <Zap className="w-4 h-4 text-blue-500" />
              Smart Matches ({wizardState.smartMatches.length})
            </h4>
            <p className="text-xs text-muted-foreground mb-2">
              These employees were matched by name. Accept to link or reject to create new.
            </p>
            {wizardState.smartMatches.map((match) => {
              const resolution = wizardState.smartMatchResolutions.get(match.dotyposEmployee.id);
              return (
                <Card key={match.dotyposEmployee.id} className="border-blue-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">
                          {match.dotyposEmployee.name}
                        </CardTitle>
                        <CardDescription>
                          {match.dotyposEmployee.email || 'No email'}
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="border-blue-500 text-blue-700">
                        {match.similarity}% match
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                      <div>
                        <p className="font-semibold mb-1">From Dotypos:</p>
                        <p className="text-muted-foreground">{match.dotyposEmployee.name}</p>
                        <p className="text-muted-foreground">{match.dotyposEmployee.email || 'No email'}</p>
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Suggested Match:</p>
                        <p className="text-muted-foreground">{match.suggestedEmployee.name}</p>
                        <p className="text-muted-foreground">{match.suggestedEmployee.email}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={resolution === 'accept' ? 'default' : 'outline'}
                        onClick={() => handleSmartMatchResolution(match.dotyposEmployee.id, 'accept')}
                        className="flex-1"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant={resolution === 'reject' ? 'destructive' : 'outline'}
                        onClick={() => handleSmartMatchResolution(match.dotyposEmployee.id, 'reject')}
                        className="flex-1"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                    {resolution === 'accept' && (
                      <p className="text-xs text-green-600 mt-2">
                        Will update existing employee with Dotypos data
                      </p>
                    )}
                    {resolution === 'reject' && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Will need email resolution in next step
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Non-conflict employees */}
        {nonConflictEmployees.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              New Employees ({nonConflictEmployees.length})
            </h4>
            <div className="space-y-1">
              {nonConflictEmployees.slice(0, 5).map((emp) => (
                <div
                  key={emp.id}
                  className="flex items-center justify-between p-2 rounded border"
                >
                  <div>
                    <p className="text-sm font-medium">{emp.name}</p>
                    <p className="text-xs text-muted-foreground">{emp.email || 'No email'}</p>
                  </div>
                  <Badge variant="outline">{emp.enabled ? 'Active' : 'Inactive'}</Badge>
                </div>
              ))}
              {nonConflictEmployees.length > 5 && (
                <p className="text-xs text-muted-foreground text-center pt-2">
                  ... and {nonConflictEmployees.length - 5} more
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderResolveEmailsStep = () => {
    const missingEmails = wizardState.emailIssues.filter(i => i.issueType === 'missing');
    const duplicateInTaginn = wizardState.emailIssues.filter(i => i.issueType === 'duplicate_in_taginn');
    const duplicateInBatch = wizardState.emailIssues.filter(i => i.issueType === 'duplicate_in_batch');

    return (
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Resolve Email Issues</h3>
            <p className="text-sm text-muted-foreground">
              {wizardState.emailIssues.length} employee(s) need email resolution
            </p>
          </div>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Employees with placeholder emails won't be able to log into the Employee Portal.
            Enter a real email address if they need portal access.
          </AlertDescription>
        </Alert>

        {/* Missing emails */}
        {missingEmails.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <Mail className="w-4 h-4 text-orange-500" />
              Missing Email Address ({missingEmails.length})
            </h4>
            {missingEmails.map((issue) => {
              const resolution = getEmailResolution(issue.dotyposEmployee.id);
              return (
                <Card key={issue.dotyposEmployee.id} className="border-orange-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">
                          {issue.dotyposEmployee.name}
                        </CardTitle>
                        <CardDescription>No email in Dotypos</CardDescription>
                      </div>
                      <Badge>Missing Email</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <RadioGroup
                      value={resolution?.resolutionType || ''}
                      onValueChange={(value) =>
                        handleEmailResolution(
                          issue.dotyposEmployee.id,
                          value as EmailResolutionType
                        )
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="manual" id={`manual-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`manual-${issue.dotyposEmployee.id}`} className="font-normal">
                          Enter email manually
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="assimilate" id={`assimilate-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`assimilate-${issue.dotyposEmployee.id}`} className="font-normal">
                          Link to existing TagInn employee
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="generate_placeholder" id={`placeholder-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`placeholder-${issue.dotyposEmployee.id}`} className="font-normal text-muted-foreground">
                          Generate placeholder (no portal access)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="skip" id={`skip-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`skip-${issue.dotyposEmployee.id}`} className="font-normal">
                          Skip this employee
                        </Label>
                      </div>
                    </RadioGroup>

                    {resolution?.resolutionType === 'manual' && (
                      <div className="pl-6">
                        {(() => {
                          const validationError = resolution.manualEmail
                            ? getEmailValidationError(resolution.manualEmail, issue.dotyposEmployee.id)
                            : null;
                          return (
                            <>
                              <Input
                                type="email"
                                placeholder="employee@example.com"
                                value={resolution.manualEmail || ''}
                                onChange={(e) =>
                                  handleEmailResolution(
                                    issue.dotyposEmployee.id,
                                    'manual',
                                    e.target.value
                                  )
                                }
                                className={cn(validationError && 'border-red-500')}
                              />
                              {validationError && (
                                <p className="text-xs text-red-500 mt-1">{validationError}</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {resolution?.resolutionType === 'assimilate' && (
                      <div className="pl-6">
                        <Select
                          value={resolution.linkToEmployeeId || ''}
                          onValueChange={(value) =>
                            handleEmailResolution(
                              issue.dotyposEmployee.id,
                              'assimilate',
                              undefined,
                              value
                            )
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select existing employee..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getAvailableEmployeesForAssimilation(issue.dotyposEmployee.id).map((emp) => (
                              <SelectItem key={emp.id} value={emp.id}>
                                {emp.name} ({emp.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {resolution.linkToEmployeeId && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Will update this employee with Dotypos data
                          </p>
                        )}
                      </div>
                    )}

                    {resolution?.resolutionType === 'generate_placeholder' && (
                      <p className="text-xs text-muted-foreground pl-6">
                        Will use: {DotyposService.generatePlaceholderEmail(issue.dotyposEmployee.id)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Duplicate in TagInn */}
        {duplicateInTaginn.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              Email Already Exists in TagInn ({duplicateInTaginn.length})
            </h4>
            {duplicateInTaginn.map((issue) => {
              const resolution = getEmailResolution(issue.dotyposEmployee.id);
              return (
                <Card key={issue.dotyposEmployee.id} className="border-red-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">
                          {issue.dotyposEmployee.name}
                        </CardTitle>
                        <CardDescription>{issue.dotyposEmployee.email}</CardDescription>
                      </div>
                      <Badge variant="destructive">Duplicate Email</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-muted-foreground mb-2">
                      This email is already used by: <strong>{issue.existingEmployee?.name}</strong>
                    </div>
                    <RadioGroup
                      value={resolution?.resolutionType || ''}
                      onValueChange={(value) =>
                        handleEmailResolution(
                          issue.dotyposEmployee.id,
                          value as EmailResolutionType,
                          undefined,
                          value === 'link_existing' ? issue.existingEmployee?.id : undefined
                        )
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="link_existing" id={`link-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`link-${issue.dotyposEmployee.id}`} className="font-normal">
                          Link to existing employee (update with Dotypos data)
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="manual" id={`diff-email-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`diff-email-${issue.dotyposEmployee.id}`} className="font-normal">
                          Enter a different email
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="skip" id={`skip-dup-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`skip-dup-${issue.dotyposEmployee.id}`} className="font-normal">
                          Skip this employee
                        </Label>
                      </div>
                    </RadioGroup>

                    {resolution?.resolutionType === 'manual' && (
                      <div className="pl-6">
                        {(() => {
                          const validationError = resolution.manualEmail
                            ? getEmailValidationError(resolution.manualEmail, issue.dotyposEmployee.id)
                            : null;
                          return (
                            <>
                              <Input
                                type="email"
                                placeholder="different@example.com"
                                value={resolution.manualEmail || ''}
                                onChange={(e) =>
                                  handleEmailResolution(
                                    issue.dotyposEmployee.id,
                                    'manual',
                                    e.target.value
                                  )
                                }
                                className={cn(validationError && 'border-red-500')}
                              />
                              {validationError && (
                                <p className="text-xs text-red-500 mt-1">{validationError}</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Duplicate in batch */}
        {duplicateInBatch.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-yellow-500" />
              Duplicate Email in Import ({duplicateInBatch.length})
            </h4>
            {duplicateInBatch.map((issue) => {
              const resolution = getEmailResolution(issue.dotyposEmployee.id);
              return (
                <Card key={issue.dotyposEmployee.id} className="border-yellow-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">
                          {issue.dotyposEmployee.name}
                        </CardTitle>
                        <CardDescription>{issue.dotyposEmployee.email}</CardDescription>
                      </div>
                      <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                        Batch Duplicate
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-muted-foreground mb-2">
                      Same email as: {issue.duplicateEmployees?.map(e => e.name).join(', ')}
                    </div>
                    <RadioGroup
                      value={resolution?.resolutionType || ''}
                      onValueChange={(value) =>
                        handleEmailResolution(
                          issue.dotyposEmployee.id,
                          value as EmailResolutionType
                        )
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="manual" id={`batch-manual-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`batch-manual-${issue.dotyposEmployee.id}`} className="font-normal">
                          Enter a different email
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="skip" id={`batch-skip-${issue.dotyposEmployee.id}`} />
                        <Label htmlFor={`batch-skip-${issue.dotyposEmployee.id}`} className="font-normal">
                          Skip this employee
                        </Label>
                      </div>
                    </RadioGroup>

                    {resolution?.resolutionType === 'manual' && (
                      <div className="pl-6">
                        {(() => {
                          const validationError = resolution.manualEmail
                            ? getEmailValidationError(resolution.manualEmail, issue.dotyposEmployee.id)
                            : null;
                          return (
                            <>
                              <Input
                                type="email"
                                placeholder="unique@example.com"
                                value={resolution.manualEmail || ''}
                                onChange={(e) =>
                                  handleEmailResolution(
                                    issue.dotyposEmployee.id,
                                    'manual',
                                    e.target.value
                                  )
                                }
                                className={cn(validationError && 'border-red-500')}
                              />
                              {validationError && (
                                <p className="text-xs text-red-500 mt-1">{validationError}</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderImportStep = () => (
    <div className="space-y-4 flex flex-col items-center justify-center py-8">
      <Loader2 className="w-12 h-12 animate-spin text-primary" />
      <h3 className="text-lg font-semibold">Importing Employees...</h3>
      <p className="text-sm text-muted-foreground">
        Please wait while we import your employees from Dotypos
      </p>
      <Progress value={undefined} className="w-full" />
    </div>
  );

  const renderSyncAttendanceStep = () => (
    <div className="space-y-4">
      <div className="flex flex-col items-center justify-center py-4">
        <RefreshCw className={cn("w-16 h-16 text-primary mb-4", isSyncing && "animate-spin")} />
        <h3 className="text-xl font-semibold mb-2">Sync Attendance Data</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Now let's sync your employee attendance records from Dotypos.
          This will import all available historical attendance data.
        </p>
      </div>

      {wizardState.summary && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            Employee import complete: {wizardState.summary.created} created,
            {' '}{wizardState.summary.updated} updated, {wizardState.summary.skipped} skipped.
            Now configure attendance sync below.
          </AlertDescription>
        </Alert>
      )}

      {!isSyncing && !attendanceSyncResult && (
        <div className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Sync Mode</Label>
            <RadioGroup
              value={syncMode}
              onValueChange={(value) => setSyncMode(value as AttendanceSyncMode)}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="create_shifts" id="create_shifts" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="create_shifts" className="font-medium flex items-center gap-2 cursor-pointer">
                    <Calendar className="w-4 h-4 text-primary" />
                    Create new shifts from attendance
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Automatically creates shifts based on check-in/check-out pairs from Dotypos.
                    Best for first-time setup or when you don't have existing shifts.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="match_existing" id="match_existing" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="match_existing" className="font-medium flex items-center gap-2 cursor-pointer">
                    <Link2 className="w-4 h-4 text-primary" />
                    Match attendance to existing shifts
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Links Dotypos attendance records to your existing shifts based on employee email and date.
                    Best when you already have shifts scheduled in the system.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          <div className="text-center pt-2">
            <Button onClick={handleSyncAttendance} size="lg">
              <RefreshCw className="w-4 h-4 mr-2" />
              {syncMode === 'create_shifts' ? 'Create Shifts from Attendance' : 'Match Attendance to Shifts'}
            </Button>
          </div>
        </div>
      )}

      {isSyncing && (
        <div className="flex flex-col items-center py-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-2" />
          <p className="text-sm text-muted-foreground">
            {syncMode === 'create_shifts'
              ? 'Creating shifts from attendance records...'
              : 'Matching attendance to existing shifts...'}
          </p>
        </div>
      )}
    </div>
  );

  const renderCompleteStep = () => {
    if (!wizardState.summary) return null;

    const { summary } = wizardState;
    const hasErrors = summary.failed > 0;

    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-4">
          {hasErrors ? (
            <AlertCircle className="w-16 h-16 text-orange-500 mb-4" />
          ) : (
            <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
          )}
          <h3 className="text-xl font-semibold mb-2">
            {hasErrors ? 'Setup Completed with Errors' : 'Setup Successful!'}
          </h3>
          <p className="text-sm text-muted-foreground text-center">
            Employees imported and attendance data synced. Time tracking is now enabled with Dotypos.
          </p>
        </div>

        <Separator />

        <div>
          <h4 className="font-semibold mb-3">Employee Import Summary</h4>
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs">Created</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold text-green-600">{summary.created}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs">Updated</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold text-blue-600">{summary.updated}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs">Skipped</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold text-gray-600">{summary.skipped}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs">Failed</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xl font-bold text-red-600">{summary.failed}</p>
              </CardContent>
            </Card>
          </div>

          {/* Show warning for employees with placeholder emails */}
          {(() => {
            const placeholderCount = Array.from(wizardState.emailResolutions.values())
              .filter(r => r.resolutionType === 'generate_placeholder').length;

            if (placeholderCount > 0) {
              return (
                <Alert className="mt-4">
                  <UserX className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{placeholderCount} employee(s)</strong> were imported with placeholder emails
                    and won't be able to access the Employee Portal. You can update their emails
                    later in the employee management section.
                  </AlertDescription>
                </Alert>
              );
            }
            return null;
          })()}
        </div>

        {attendanceSyncResult && (
          <>
            <Separator />
            <div>
              <h4 className="font-semibold mb-3">
                {syncMode === 'create_shifts' ? 'Shift Creation Summary' : 'Attendance Matching Summary'}
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs">Fetched</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xl font-bold text-primary">{attendanceSyncResult.total_fetched}</p>
                  </CardContent>
                </Card>

                {'shifts_created' in attendanceSyncResult ? (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs">Shifts Created</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xl font-bold text-green-600">{attendanceSyncResult.shifts_created}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs">Time Logs</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xl font-bold text-blue-600">{attendanceSyncResult.time_logs_created}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs">Duplicates Skipped</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xl font-bold text-gray-600">{attendanceSyncResult.skipped_duplicates}</p>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs">Matched to Shifts</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xl font-bold text-green-600">{attendanceSyncResult.matched_to_shifts}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs">Unmatched</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xl font-bold text-orange-600">{attendanceSyncResult.unmatched}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs">Skipped</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xl font-bold text-gray-600">{attendanceSyncResult.skipped}</p>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
              {attendanceSyncResult.errors > 0 && (
                <Alert variant="destructive" className="mt-4">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    {attendanceSyncResult.errors} error(s) occurred during sync.
                  </AlertDescription>
                </Alert>
              )}
              {'skipped_no_mapping' in attendanceSyncResult && attendanceSyncResult.skipped_no_mapping > 0 && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {attendanceSyncResult.skipped_no_mapping} attendance records were skipped because the employees
                    are not imported into TagInn. Dotypos employee IDs: {attendanceSyncResult.unmapped_employee_ids?.join(', ')}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </>
        )}

        {hasErrors && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              {summary.failed} employee(s) failed to import. Check the details below.
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Import Employees from Dotypos
          </DialogTitle>
          <DialogDescription>
            Import employee data from your Dotypos POS system
          </DialogDescription>
        </DialogHeader>

        {renderStepIndicator()}

        <div className="min-h-[300px]">
          {wizardState.step === 'fetch' && renderFetchStep()}
          {wizardState.step === 'review' && renderReviewStep()}
          {wizardState.step === 'resolve_emails' && renderResolveEmailsStep()}
          {wizardState.step === 'import' && renderImportStep()}
          {wizardState.step === 'sync_attendance' && renderSyncAttendanceStep()}
          {wizardState.step === 'complete' && renderCompleteStep()}
        </div>

        {wizardState.error && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{wizardState.error}</AlertDescription>
          </Alert>
        )}

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            {wizardState.step === 'review' && (
              <Button
                onClick={async () => {
                  // Check if all smart matches are resolved
                  const unresolvedSmartMatches = wizardState.smartMatches.filter(
                    m => !wizardState.smartMatchResolutions.has(m.dotyposEmployee.id)
                  );

                  if (unresolvedSmartMatches.length > 0) {
                    toast.error('Unresolved Matches', {
                      description: `Please accept or reject all ${unresolvedSmartMatches.length} smart match(es)`,
                    });
                    return;
                  }

                  // Get rejected smart matches - they need email resolution
                  const rejectedMatches = wizardState.smartMatches.filter(
                    m => wizardState.smartMatchResolutions.get(m.dotyposEmployee.id) === 'reject'
                  );

                  // Calculate total email issues including rejected smart matches
                  const totalEmailIssues = wizardState.emailIssues.length +
                    rejectedMatches.filter(m => !m.dotyposEmployee.email).length;

                  if (totalEmailIssues > 0 || rejectedMatches.length > 0) {
                    // Need to add rejected smart matches to email issues
                    const additionalIssues = rejectedMatches
                      .filter(m => !m.dotyposEmployee.email)
                      .map(m => ({
                        dotyposEmployee: m.dotyposEmployee,
                        issueType: 'missing' as const,
                      }));

                    setWizardState((prev) => ({
                      ...prev,
                      step: 'resolve_emails',
                      emailIssues: [...prev.emailIssues, ...additionalIssues],
                    }));
                  } else {
                    handleImport();
                  }
                }}
              >
                {(() => {
                  const unresolvedSmartMatches = wizardState.smartMatches.filter(
                    m => !wizardState.smartMatchResolutions.has(m.dotyposEmployee.id)
                  ).length;

                  if (unresolvedSmartMatches > 0) {
                    return (
                      <>
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Resolve Smart Matches ({unresolvedSmartMatches})
                      </>
                    );
                  }

                  const rejectedWithoutEmail = wizardState.smartMatches.filter(
                    m => wizardState.smartMatchResolutions.get(m.dotyposEmployee.id) === 'reject' && !m.dotyposEmployee.email
                  ).length;
                  const totalIssues = wizardState.emailIssues.length + rejectedWithoutEmail;

                  if (totalIssues > 0) {
                    return (
                      <>
                        <ChevronRight className="w-4 h-4 mr-2" />
                        Resolve Email Issues ({totalIssues})
                      </>
                    );
                  }

                  return (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Start Import
                    </>
                  );
                })()}
              </Button>
            )}

            {wizardState.step === 'resolve_emails' && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => setWizardState((prev) => ({ ...prev, step: 'review' }))}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!allEmailIssuesResolved()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Start Import
                </Button>
              </>
            )}

            {wizardState.step === 'complete' && (
              <Button onClick={handleClose}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Done
              </Button>
            )}

            {(wizardState.step === 'fetch' && wizardState.error) && (
              <Button variant="ghost" onClick={handleClose}>
                Close
              </Button>
            )}

            {wizardState.step === 'review' && (
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
